import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import Title from "../../../common/Title";
import OPNActivateList from "./ActivateList";
import TerminateBatch from "../../../common/TerminateBatch";

import { copyData } from "../../../../utils/copy";
import useAxios from "../../../../hooks/useAxios";
import { CLIENTS } from "../../../../constants/value";
import {
  __getActivate,
  __export,
  __retryOperation,
  __checkStatus,
} from "../../../../api/sim";
import useAlert from "../../../../hooks/useAlert";

const OPNActivateBatch = () => {
  const { batch_id } = useParams();
  const [data, setData] = useState([]);

  const COPY_KEYS = [
    "id",
    "batch_id",
    "iccid",
    "status",
    "msisdn",
    "last_updated",
  ];

  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 20000,
    total: 1,
  });
  const { setAlertMessage } = useAlert();
  const [retryResponse, retryCall, retryLoading] = useAxios({
    ...__retryOperation(batch_id),
  });
  const [checkStatusResponse, checkStatusCall, checkStatusLoading] = useAxios({
    ...__checkStatus(batch_id),
  });
  const [response, call, loading] = useAxios({ ...__getActivate });
  const [exportResponse, exportCall, exportLoading] = useAxios({ ...__export });

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        client: CLIENTS.opn,
        page_size: 20000,
        batch_id: batch_id,
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
  }, []);

  useEffect(() => {
    if (response && response.status) {
      setMetaData({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
      });
      setData(response.data.data);
    }
  }, [response]);

  useEffect(() => {
    if (exportResponse && exportResponse.status) {
      fileDownload(
        exportResponse.data,
        `opn-activate_export-${batch_id}.csv`
      );
    }
  }, [exportResponse]);

  useEffect(() => {
    if (retryResponse && retryResponse.status) {
      setAlertMessage({
        type: "success",
        message: "Activation retry queued",
      });
    }
  }, [retryResponse]);

  useEffect(() => {
    if (checkStatusResponse && checkStatusResponse.status) {
      setAlertMessage({
        type: "success",
        message: "Activation status update queued",
      });
    }
  }, [checkStatusResponse]);

  return (
    <Box sx={{ width: "100%" }}>
      <Title
        variant="h5"
        content={`OPN Activate Batch ID: ${batch_id}`}
        gutterBottom
      />
      {response && (
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="primary"
                disabled={loading || exportLoading || checkStatusLoading}
                onClick={() =>
                  exportCall({
                    responseType: "blob",
                    params: {
                      action: "activate",
                      batch_id: batch_id,
                      client: CLIENTS.opn,
                    },
                  })
                }
              >
                <FileDownloadIcon sx={{ mr: "5px" }} /> Export
              </Button>
            </Stack>
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="secondary"
                disabled={loading || exportLoading || checkStatusLoading}
                onClick={() => copyData(data, COPY_KEYS)}
              >
                <ContentCopyIcon sx={{ mr: "5px" }} /> Copy Data
              </Button>
            </Stack>
          </Grid>
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="primary"
                disabled={loading || exportLoading || checkStatusLoading}
                onClick={() =>
                  retryCall({
                    params: {
                      client: "opn",
                      operation: "activate",
                    },
                  })
                }
              >
                Retry
              </Button>
            </Stack>
          </Grid>
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="primary"
                disabled={loading || exportLoading || checkStatusLoading}
                onClick={() =>
                  checkStatusCall({
                    params: {
                      client: "opn",
                      operation: "activate",
                    },
                  })
                }
              >
                Recheck Status
              </Button>
            </Stack>
          </Grid>
          <Grid item sx={{ mb: "10px" }}>
            <TerminateBatch batch_id={batch_id} />
          </Grid>
        </Grid>
      )}
      {response ? (
        <>
          <OPNActivateList
            data={data}
            loading={loading}
            meta={{ ...metaData, page_size: 100 }}
            client={true}
            handleColumnClick={(key) => copyData(data, [key])}
          />
        </>
      ) : (
        <Title content="Please wait..." variant="subtitle2" gutterBottom />
      )}
    </Box>
  );
};

export default OPNActivateBatch;
